import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { filter, switchMap } from 'rxjs';

import { SvgIconService } from './svg-icon.service';

@Component({
  selector: 'app-icon',
  templateUrl: './svg-icon.html',
  styleUrl: './svg-icon.scss',
  imports: [NgStyle],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SvgIcon {
  private readonly svgIconService = inject(SvgIconService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  readonly name = input.required<string>();
  readonly size = input<string>('24');
  readonly color = input<string>('currentColor');
  readonly gradient = input<string>();

  private readonly isVisible = signal(false);
  private readonly observer?: IntersectionObserver;

  private readonly request = computed(() => ({
    name: this.name(),
    size: this.size(),
    color: this.color(),
    gradient: this.gradient(),
    isVisible: this.isVisible(),
  }));

  protected readonly sizeStyle = computed(() => {
    const size = this.size();
    return size.startsWith('var(') ? size : `${size}px`;
  });

  protected readonly iconSvg = toSignal(
    toObservable(this.request).pipe(
      filter((req) => !!req.name && req.isVisible),
      switchMap((req) => this.svgIconService.getIcon(req.name, req.size, req.color, req.gradient)),
    ),
    { initialValue: null },
  );

  constructor() {
    const el = this.elementRef.nativeElement;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.ngZone.run(() => this.isVisible.set(true));
          this.observer?.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0,
      },
    );

    // Delay observation slightly to ensure layout is complete
    requestAnimationFrame(() => {
      this.observer?.observe(el);
    });

    this.destroyRef.onDestroy(() => {
      this.observer?.disconnect();
    });
  }
}
